import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, tokenUtils } from '../services/api';
import { API_ENDPOINTS, QUERY_KEYS } from '../utils/constants';
import { showErrorToast, showSuccessToast } from '../utils/toast';

// Login mutation
// Accepts options: { silent: boolean } to suppress toast notifications
export const useLogin = (options = {}) => {
  const queryClient = useQueryClient();
  const { silent = false, ...mutationOptions } = options;

  return useMutation({
    mutationFn: async (credentials) => {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, credentials);
      return response;
    },
    onSuccess: (data, variables, context) => {
      // Store tokens
      if (data.access) {
        tokenUtils.setTokens(data.access, data.refresh);
      }
      // Store user data
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }
      // Invalidate and refetch user queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AUTH.CURRENT_USER });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS.ME });
      // Only show toast if not silent (for auto-login during signup)
      if (!silent) {
        showSuccessToast('Login successful!');
      }
      // Call custom onSuccess if provided
      if (mutationOptions.onSuccess) {
        mutationOptions.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // Error is handled in Login component, no need for duplicate toast
      // showErrorToast(error, 'Login failed. Please check your credentials.');
      // Call custom onError if provided
      if (mutationOptions.onError) {
        mutationOptions.onError(error, variables, context);
      }
    },
    ...mutationOptions,
  });
};

// Refresh token mutation
export const useRefreshToken = () => {
  return useMutation({
    mutationFn: async () => {
      const refreshToken = tokenUtils.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      const response = await apiClient.post(API_ENDPOINTS.AUTH.REFRESH, {
        refresh: refreshToken,
      });
      return response;
    },
    onSuccess: (data) => {
      if (data.access) {
        tokenUtils.setTokens(data.access, data.refresh);
      }
    },
  });
};

// Verify token query/mutation
export const useVerifyToken = () => {
  return useMutation({
    mutationFn: async (token) => {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.VERIFY, { token });
      return response;
    },
  });
};

// Logout mutation
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const refreshToken = tokenUtils.getRefreshToken();
      if (refreshToken) {
        try {
          await apiClient.post(API_ENDPOINTS.USERS.LOGOUT, { refresh: refreshToken });
        } catch (error) {
          // Even if logout fails, clear local storage
          console.warn('Logout API call failed:', error);
        }
      }
    },
    onSuccess: () => {
      // Clear tokens and user data
      tokenUtils.clearTokens();
      // Clear all queries
      queryClient.clear();
      showSuccessToast('Logged out successfully');
    },
    onError: (error) => {
      // Still clear local storage even if API call fails
      tokenUtils.clearTokens();
      queryClient.clear();
      showErrorToast(error, 'Logout completed');
    },
  });
};

